import moment from 'moment';
import isEqual from 'lodash.isequal';
import { StateTester } from '../../core/match';
import { Validator, Result } from '../../core/types/input-field';
import {
  fundingTypes,
  BankFailureTypes,
  formatAmountForDisplay,
  filterExistingInvestmentsFromNew,
  I_CLASS_MINIMUM,
  isContributory
} from './utils';
import {
  isEmptyObject,
  removeCommasAndParseFloat,
  withDefault
} from '../../core/util';
import { convertDOBToAge, isDateInFuture, isValidDate } from './dateUtils';
import { linkOptionLocked } from '../../screens/FundingScreen/util';
import {
  DEFAULT_CURR_YR_MAX,
  DEFAULT_PRIOR_YR_MAX,
  getExistingUserRemainingContribLimit
} from '../../components/ContributionLimits/utils';
import { meetsSummitRewardsTierForIClass } from './summit.testers';

export const AGE_ERROR_MSG = 'Age must be 18 or older';
export const MINOR_AGE_ERROR_MSG = 'Age must be under 18 yrs for minor';
export const SSN_ERROR_MSG = 'Invalid Social Security Number';
export const MIN_CONTRIBUTION_ERR_MSG =
  'You must enter a dollar amount of $1,000 or more in order to continue';
export const MAX_CONTRIBUTION_ERR_MSG =
  'Investment amount exceeds IRS imposed contribution limit';
export const CONTRIBUTORY_EXCEEDS_INV_ERR_MSG =
  'Cannot exceed your remaining contributions';

// eslint-disable-next-line no-control-regex
/* eslint-disable prefer-regex-literals */
const EMAIL_REGEX = new RegExp(
  /^[_A-Za-z0-9-]+(\.[_A-Za-z0-9-]+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9]+)*(\.[A-Za-z]{2,})$/i
);
const FORMER_EMPLOYER_NAME = new RegExp(/^[A-Za-z0-9]+[ A-Za-z0-9-_.,'*&]*$/);
export const DOB_REGEX = new RegExp(
  /^([0-9]{1,2}[/-][0-9]{1,2}[/-][0-9][0-9][0-9][0-9])?$/
);

const EIN_REGEX = /^\d{2}-\d{7}$/;
export const SSN_REGEX = /^\d{3}-\d{2}-\d{4}$/;
const MASKED_SSN_REGEX = /^[*]{3}[*]{2}\d{4}$/;
const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9-]*$/;
const ACCOUNT_NUMBER_REGEX = /^[0-9]\d{1,17}$/;

// provide metadata for Flow graph
// should be used with state testers (for routing cases)
const withDescription = (fn, desc): any => {
  const proxyFn = (...args): any => fn(...args);
  proxyFn.desc = desc;
  return proxyFn;
};

export const has = (field: string): StateTester => ({ inputs }): any =>
  inputs?.[field];

export const hasURLTickers: StateTester = withDescription(
  (_, nextAppState): boolean =>
    Boolean(nextAppState?.store?.loadedData?.shoppingFunds?.length > 0),
  'User buying fund(s) from research tool'
);

export const isValidEmail: Validator = (val): Result =>
  EMAIL_REGEX.test(val) || 'Invalid Email Address';

export const isValidEIN: Validator = (val): Result =>
  EIN_REGEX.test(val) || 'Invalid Employer Indentification Number';

export const isValidSSN: Validator = (val): Result =>
  SSN_REGEX.test(val) || SSN_ERROR_MSG;

export const isValidMaskedSSN: Validator = (val, _, state): Result => {
  const isEnabled = state?.store?.enabled;
  if (isEnabled) {
    return MASKED_SSN_REGEX.test(val) || SSN_ERROR_MSG;
  }
  return isValidSSN(val);
};

export const isUniqueSSN: Validator = (val, context, state): Result => {
  const ssnWithNoDashes = val.match(/[\d]/g);

  if (
    isEmptyObject(context) &&
    ssnWithNoDashes &&
    isEqual(ssnWithNoDashes.join(''), state?.store?.app?.customer?.taxID)
  ) {
    return 'Social security number should not be the same';
  }

  const SSNPropertyNames = [
    'beneTaxID',
    'ssn',
    'jointOwnerSsn',
    'coTrusteeSsn'
  ];
  return (
    Object.entries<string>(context)
      .filter(([entry]) => SSNPropertyNames.some(name => entry.includes(name)))
      .map(ssn => ssn[1])
      .reduce((emptyString, _, __, arrayOfSSNs) => {
        const allBeneSSNs = [...arrayOfSSNs];

        // check if there is still one more copy of the current SSN in the array. If there is
        // then it's a duplicate which we don't want.
        const isSSNDuplicated = allBeneSSNs.includes(val);

        if (
          (ssnWithNoDashes &&
            isEqual(
              ssnWithNoDashes.join(''),
              state?.store?.app?.customer?.taxID
            )) ||
          isSSNDuplicated
        ) {
          return 'Social security number should not be the same';
        }

        return emptyString;
      }, '') || SSN_REGEX.test(val)
  );
};

export const validateDob = (value: string): string => {
  let errorMessage = '';
  const age = convertDOBToAge(value);

  if (isDateInFuture(value)) {
    errorMessage = 'Date cannot be in the future';
  } else if (!isValidDate(value) || age < 18 || age > 110) {
    errorMessage = 'Check your date of birth';
  }
  return errorMessage;
};

export const isValidDob: Validator = (val): Result => {
  if (val.trim().length === 0) {
    return 'Required';
  }
  if (!DOB_REGEX.test(val)) {
    return 'Invalid date of birth format';
  }

  return validateDob(val) || true;
};

export const validateBeneOrSuccesorDob = (value: string): string => {
  let errorMessage = '';
  const age = convertDOBToAge(value);
  if (isDateInFuture(value)) {
    errorMessage = 'Date cannot be in the future';
  } else if (!isValidDate(value) || age > 110) {
    errorMessage = 'Check your date of birth';
  }
  return errorMessage;
};

export const isValidBeneorSucessorDob: Validator = (val): Result => {
  if (val.trim().length === 0) {
    return 'Required';
  }
  if (!DOB_REGEX.test(val)) {
    return 'Invalid date of birth format';
  }

  return validateBeneOrSuccesorDob(val) || true;
};

export const isAlphanumeric: Validator = (val): Result => {
  if (val.trim().length === 0) {
    return 'Required';
  }

  return ALPHANUMERIC_REGEX.test(val) || 'Account number must be alphanumeric';
};

export const isValidAccountNumber: Validator = (val): Result => {
  if (val.trim().length === 0) {
    return 'Required';
  }

  return ACCOUNT_NUMBER_REGEX.test(val) || 'Invalid Account Number';
};

/**
 * Function which returns true if user is loggedIn
 * @returns {boolean}
 * */
export const isAuthenticated = (): StateTester =>
  withDescription(
    ({ store: { authenticated } }): boolean => Boolean(authenticated),
    'User is logged in'
  );

export const trustUsernameValidation = [
  (value): Result =>
    new RegExp('^(.){6,}$', 'gi').test(value) ||
    'Must be at least 6 characters long',
  (value): Result =>
    new RegExp('^(?!.*[\\s+"%&\'`;=^,{}<>()])(.*)', 'gi').test(value) ||
    'Cannot contain spaces or the following characters: " % & \' + ; = , ^ ` > < {} ()',
  (value, _, state): Result => {
    const taxID = state?.store?.app?.trustee?.taxID;
    const curValue = value?.toLowerCase();
    return (
      !curValue.includes(taxID) ||
      'Cannot contain or be the same as your Social Security number'
    );
  },
  (value, context): Result => {
    const currValue = value?.toLowerCase();
    const currPassword = context?.password?.toLowerCase();
    return (
      !currValue.includes(currPassword) ||
      'Cannot contain or be the same as your password'
    );
  }
];

export const usernameValidation = [
  (value): Result =>
    new RegExp('^(.){6,}$', 'gi').test(value) ||
    'Must be at least 6 characters long',
  (value): Result =>
    new RegExp('^(?!.*[\\s+"%&\'`;=^,{}<>()?#])(.*)', 'gi').test(value) ||
    'Cannot contain spaces or the following characters: " % & \' + ; = , ^ ` > < {} () ? #',
  (value): Result =>
    new RegExp('(?=(.*[a-zA-Z]){1})^.+$', 'g').test(value) ||
    'Must contain at least one letter',
  (value): Result =>
    new RegExp('^([^0-9]*[0-9]?[^0-9]*){7}$', 'g').test(value) ||
    'Cannot contain more than 7 digits',
  (value, _, state): Result => {
    const taxID = state?.store?.app?.customer?.taxID;
    const curValue = value?.toLowerCase();
    return (
      !curValue.includes(taxID) ||
      'Cannot contain or be the same as your Social Security number'
    );
  },
  (value, context): Result => {
    const currValue = value?.toLowerCase();
    const currPassword = context?.password?.toLowerCase();
    return (
      !currValue.includes(currPassword) ||
      'Cannot contain or be the same as your password'
    );
  }
];

export const passwordValidation = [
  (value): Result =>
    new RegExp('^(.){8,32}$', 'g').test(value) ||
    'Must be between 8 and 32 characters long',
  (value, context): Result => {
    const curValue = value?.toLowerCase();
    const curUsername = context?.username?.toLowerCase();
    return (
      !curValue.includes(curUsername) ||
      'Cannot contain or be the same as your user name'
    );
  },
  (value): Result =>
    new RegExp('(?=(.*[a-zA-Z]){2})^.+$', 'g').test(value) ||
    'Must contain at least two letters',
  (value): Result =>
    new RegExp('(?=(.*[0-9]){2})^.+$', 'g').test(value) ||
    'Must contain at least two numbers',
  (value): Result =>
    new RegExp('^(?!.*[\\s\\\\"%&\';=^`.,{}/<>[\\]\\-])(.*)', 'gi').test(
      value
    ) ||
    'Cannot contain contain spaces or the following characters: ` . , + “ % & ‘ \\ ; = ^ {} / <> [] -',
  (value): Result =>
    new RegExp('^[\u0020-\u007A]+$', 'g').test(value) ||
    'Cannot contain any Unicode characters',
  (value): Result => {
    const charArray: string[] = value.split('');
    const counts: { [key: string]: number } = charArray.reduce(
      (acc, cur) => ({ ...acc, [cur]: acc[cur] ? acc[cur] + 1 : 1 }),
      {}
    );
    return (
      Object.values(counts).some(count => count > 3) &&
      'Cannot have more than three repetitive characters, regardless of where they appear in the password'
    );
  }
];

export const isValidFormerEmployer: Validator = (val): Result =>
  FORMER_EMPLOYER_NAME.test(val) || 'Invalid Former Employer';

export const meetsMinimumInvestment = (val): Result => {
  return removeCommasAndParseFloat(val) >= 1000 || MIN_CONTRIBUTION_ERR_MSG;
};

// TODO [2021-10-13]: break this function up into multiple functions to pass complexity barrier
/* eslint-disable complexity */
export const meetsMaximumPriorYearInvestment: Validator = (
  val,
  context,
  state
): Result => {
  const value = removeCommasAndParseFloat(val);
  const otherFieldAmount = removeCommasAndParseFloat(
    context?.currentYearAmount
  );
  const combinedAmount = value + otherFieldAmount;
  const meetsInvestmentRequirements =
    value >= 1000 || otherFieldAmount >= 1000 || combinedAmount >= 1000;

  const retailEnabled = state?.store?.retailEnabled;
  const accountType = state?.store?.accountType;
  const ageFromStoreAtEndOfCurrentYear = convertDOBToAge(
    state?.store?.app?.customer?.dob
  );
  const ageFromContextAtEndOfCurrentYear = convertDOBToAge(context?.dob);
  const age = withDefault(
    ageFromStoreAtEndOfCurrentYear,
    ageFromContextAtEndOfCurrentYear
  ); /* account for pre-loaded data in store before defaulting to input value */

  const defaultPriorYearMaxLimitBasedOnAge = DEFAULT_PRIOR_YR_MAX(age);
  const {
    priorYearMaximumLimit = 0,
    priorYearRemainingContributions = 0,
    priorYearContributionsSoFar = 0,
    priorYearRemainingAutomaticPurchases = 0,
    newUserHigherLimitPriorYear = 0,
    newUserLowerLimitPriorYear = 0
  } = state?.store?.loadedData?.contributionLimits || {};

  const newUserMax =
    ageFromContextAtEndOfCurrentYear > 50
      ? newUserHigherLimitPriorYear
      : newUserLowerLimitPriorYear;
  // set default prior year limit incase of contrib call
  // service failure for guest users
  const defaultNewUserPriorYearMax =
    newUserMax >= 0 ? newUserMax : defaultPriorYearMaxLimitBasedOnAge;

  // handle the contrib call service failure for existing users
  // might be a valid use case for RPS only users
  const existingUserMax =
    priorYearMaximumLimit >= 0
      ? priorYearMaximumLimit
      : defaultPriorYearMaxLimitBasedOnAge;
  const max = retailEnabled ? existingUserMax : defaultNewUserPriorYearMax;

  const priorYearExistingUserRemainingLimit = getExistingUserRemainingContribLimit(
    priorYearContributionsSoFar,
    priorYearRemainingAutomaticPurchases,
    priorYearMaximumLimit,
    priorYearRemainingContributions
  );

  if (retailEnabled && value > priorYearExistingUserRemainingLimit)
    return `Investment amount exceeds your remaining available contributions of ${
      priorYearExistingUserRemainingLimit !== 0
        ? formatAmountForDisplay(priorYearExistingUserRemainingLimit)
        : `$${priorYearExistingUserRemainingLimit}`
    }`;

  if (value > max) return MAX_CONTRIBUTION_ERR_MSG;
  if (value === 0 && otherFieldAmount > 0) return true;
  if (isContributory(accountType))
    return (
      meetsInvestmentRequirements ||
      `You must enter a dollar amount between $1000-$${
        retailEnabled ? priorYearExistingUserRemainingLimit : max
      } to continue.`
    );

  return meetsInvestmentRequirements || MIN_CONTRIBUTION_ERR_MSG;
};

export const meetsMaximumCurrentYearInvestment: Validator = (
  val,
  context,
  state
): Result => {
  const value = removeCommasAndParseFloat(val);
  const otherFieldAmount = removeCommasAndParseFloat(context?.priorYearAmount);
  const combinedAmount = value + otherFieldAmount;
  const meetsInvestmentRequirements =
    value >= 1000 || otherFieldAmount >= 1000 || combinedAmount >= 1000;

  const retailEnabled = state?.store?.retailEnabled;
  const accountType = state?.store?.accountType;
  const ageFromStoreAtEndOfCurrentYear = convertDOBToAge(
    state?.store?.app?.customer?.dob
  );
  const ageFromContextAtEndOfCurrentYear = convertDOBToAge(context?.dob);
  const age = withDefault(
    ageFromStoreAtEndOfCurrentYear,
    ageFromContextAtEndOfCurrentYear
  ); /* account for pre-loaded data in store before defaulting to input value */

  const defaultCurYearMaxLimitBasedOnAge = DEFAULT_CURR_YR_MAX(age);
  const {
    currentYearMaximumLimit = 0,
    currentYearRemainingContributions = 0,
    currentYearContributionsSoFar = 0,
    currentYearRemainingAutomaticPurchases = 0,
    newUserHigherLimitCurrentYear = 0,
    newUserLowerLimitCurrentYear = 0
  } = state?.store?.loadedData?.contributionLimits || {};

  const newUserMax =
    ageFromContextAtEndOfCurrentYear >= 50
      ? newUserHigherLimitCurrentYear
      : newUserLowerLimitCurrentYear;
  // set default current year limit incase of
  // contrib call service failure for guest users
  const defaultNewUserCurYearMax =
    newUserMax >= 0 ? newUserMax : defaultCurYearMaxLimitBasedOnAge;

  // handle the contrib call service failure for existing users
  // might be a valid use case for RPS only users
  const existingUserMax =
    currentYearMaximumLimit >= 0
      ? currentYearMaximumLimit
      : defaultCurYearMaxLimitBasedOnAge;
  const max = retailEnabled ? existingUserMax : defaultNewUserCurYearMax;

  const currYearExistingUserRemainingLimit = getExistingUserRemainingContribLimit(
    currentYearContributionsSoFar,
    currentYearRemainingAutomaticPurchases,
    currentYearMaximumLimit,
    currentYearRemainingContributions
  );

  if (retailEnabled && value > currYearExistingUserRemainingLimit)
    return `Investment amount exceeds your remaining available contributions of ${
      currYearExistingUserRemainingLimit !== 0
        ? formatAmountForDisplay(currYearExistingUserRemainingLimit)
        : `$${currYearExistingUserRemainingLimit}`
    }`;

  if (value > max) return MAX_CONTRIBUTION_ERR_MSG;
  if (value === 0 && otherFieldAmount > 0) return true;
  if (isContributory(accountType))
    return (
      meetsInvestmentRequirements ||
      `You must enter a dollar amount between $1000-$${
        retailEnabled ? currYearExistingUserRemainingLimit : max
      } to continue.`
    );
  return meetsInvestmentRequirements || MIN_CONTRIBUTION_ERR_MSG;
};
/* eslint-enable complexity */

export const meetsMinimumInvestmentAPP = (val): Result =>
  removeCommasAndParseFloat(val) < 50000 && 'Minimum Investment of $50,000';

export const meetsTaxableMinimumInvestment = (val): Result => {
  return (
    removeCommasAndParseFloat(val) >= 2500 ||
    'You must enter a dollar amount of $2,500 or more to continue.'
  );
};

export const meetsTrustTaxableMinimumInvestment = (val): Result => {
  return (
    removeCommasAndParseFloat(val) >= 2500 ||
    'You must enter at least $2500 in order to continue'
  );
};

export const meetsMinorAgeValidation = (val): Result => {
  if (!DOB_REGEX.test(val)) {
    return 'Invalid date of birth format';
  }

  const mDob = moment(val);
  const mNow = moment(Date.now());
  let ageFromDateofBirth = mNow.diff(mDob, 'hours');
  if (ageFromDateofBirth > 0) {
    ageFromDateofBirth = mNow.diff(mDob, 'years');
    if (!(ageFromDateofBirth >= 0 && ageFromDateofBirth < 18)) {
      return MINOR_AGE_ERROR_MSG;
    }
  } else {
    return MINOR_AGE_ERROR_MSG;
  }
  return true;
};

export const taxWithholdingValidation = (val): Result => {
  const taxWithholdingPercent = Number(val);
  return taxWithholdingPercent <= 100 || 'Please enter a value between 0-100';
};

export const userValidationFailed: StateTester = (_, nextState) => {
  const { loadedData: userStatus } = nextState?.store || {};
  return userStatus?.validationStatus === 'INVALID';
};

export const jointOwnerUserValidationFailed: StateTester = (_, nextState) => {
  const { loadedData } = nextState?.store || {};
  const invalidJointOwner = loadedData?.jointOwnersValidations?.find(
    jo => jo.validationStatus === 'INVALID'
  );
  return invalidJointOwner;
};

export const isNewFundPresent: StateTester = (_, nextState) => {
  const { loadedData } = nextState?.store || {};
  return loadedData?.newFundPresent;
};
export const chose = (val: string): StateTester =>
  withDescription(
    ({ chosen }): boolean => val === chosen,
    `User chose the ${val} path`
  );

export const choseResultingPath = (val: string): StateTester =>
  withDescription(
    ({ result }): boolean => val === result,
    `User chose to go back to the ${val} path`
  );

export const hasExistingAgreements = (existingAgreements): boolean => {
  return Array.isArray(existingAgreements)
    ? Boolean(existingAgreements.length)
    : !isEmptyObject(existingAgreements);
};

export const hasExistingFunds: StateTester = withDescription(
  ({ store: { authenticated, existingAgreements = [] } }) =>
    authenticated && hasExistingAgreements(existingAgreements),
  'User has any existing agreements/funds'
);

export const isReviewMode = (): StateTester =>
  withDescription(
    ({ store: { inReviewMode } }): boolean => inReviewMode || false,
    'User is in ReviewMode'
  );

export const isRPSRollover = (): StateTester =>
  withDescription(
    ({ store: { rpsDirectRollover } }): boolean => rpsDirectRollover || false,
    'User is in RPSMode'
  );

export const isFundOverLimit = (): StateTester =>
  withDescription(
    ({ store: { amount, purchaseLimits } }) =>
      linkOptionLocked(amount, purchaseLimits),
    'User is over 1M Fund limit and must mail a check'
  );

export const hasExistingBankAccounts = (): StateTester =>
  withDescription(
    ({ store: { existingBankAccounts } }): boolean =>
      existingBankAccounts?.length,
    'User has existing banks'
  );

export const fundingFromExistingAccount: StateTester = withDescription(
  ({
    store: {
      app: { fundingMethodType }
    }
  }): boolean => fundingMethodType === fundingTypes.FROM_EXISTING_ACCOUNT,
  'User funding from existing account'
);

export const fundingFromCheck = (): StateTester =>
  withDescription(
    ({
      store: {
        app: { fundingMethodType }
      }
    }): boolean => fundingMethodType === fundingTypes.CHECK,
    'User funding from check'
  );

export const compose = (...fns: StateTester[]): StateTester => {
  const composedFn = (prevState, nextState): boolean => {
    return fns.every(fn => fn(prevState, nextState));
  };

  composedFn.desc = fns
    .map(fn => fn.desc || fn.name || fn.toString())
    .join(' and ');
  return composedFn;
};

export const isCustodianBranchAddress: StateTester = ({ store }): boolean =>
  Boolean(store?.loadedData?.branchAddressRequired);

export const noLeadingZeros = (val): Result =>
  val.toString().match(/^[1-9]\d*$/) || 'Cannot begin with zeros';

export const amountTooLowForAPP: StateTester = ({
  store: { amount },
  // make sure they aren't adding a new amount
  inputs: { amount: inputAmount }
}) => !inputAmount && amount && amount < 50000;

export const contribLimitSvcFail: StateTester = (_, nextAppState): boolean =>
  Boolean(isEmptyObject(nextAppState?.store?.loadedData?.contributionLimits));

export const isUsernameAlreadyTaken: StateTester = (_, nextAppState): boolean =>
  Boolean(nextAppState?.store?.loadedData?.usernameTaken);

export const isUsernameCreationFailed: StateTester = (
  _,
  nextAppState
): boolean => Boolean(nextAppState?.store?.loadedData?.usernameCreationFailed);

export const isTempPasswordCreationFailed: StateTester = (
  _,
  nextAppState
): boolean =>
  Boolean(nextAppState?.store?.loadedData?.tempPasswordCreationFailed);

export const chosenAddressValidation: StateTester = (
  _,
  nextAppState
): boolean => nextAppState?.chosen?.section === 'addressValidation';

export const conformsToAgeRange = (val): Result =>
  (parseFloat(val) >= 18 && parseFloat(val) <= 110) || AGE_ERROR_MSG;

export const newAndExistingFund: StateTester = withDescription(
  ({ inputs }): boolean => inputs.new && Object.keys(inputs).length > 1,
  'User chose to add to a new fund and existing fund(s)'
);

export const newFund: StateTester = withDescription(
  ({ inputs }): boolean => inputs.new && Object.keys(inputs).length === 1,
  'User chose to add a new fund'
);

export const existingUserShouldGotoLogin: StateTester = (_, nextState) => {
  const { authenticated, loadedData: userStatus } = nextState?.store || {};
  return (userStatus?.enabled || userStatus?.redirectToLogin) && !authenticated;
};

export const existingUserShouldGotoEnablement: StateTester = (_, nextState) => {
  const { loadedData: userStatus } = nextState?.store || {};
  return userStatus?.registeredOnlyWithAgreements && !userStatus?.enabled;
};

export const existingAgreementsPresent: StateTester = (_, nextState) => {
  const { existingAgreements } = nextState?.store || {};
  return existingAgreements?.length > 0;
};

export const isPageClick: StateTester = withDescription(
  ({ chosen }) => !/^modal/.test(chosen),
  'use clicked continue on the page'
);

export const isModalClick: StateTester = withDescription(
  chose('modal'),
  'use clicked continue on the modal'
);

export const isRDFPath: StateTester = state =>
  state?.store?.app?.investmentMethod?.toLowerCase?.() === 'rdf';

export const rdfRecommendationDiffers: StateTester = (_, nextState) => {
  if (!nextState || nextState?.chosen === 'close-rdf-modal') return false;

  const {
    store: {
      chosenInvestments,
      loadedData: { rdfData = {} }
    }
  } = nextState;

  const prevRecommendation = chosenInvestments?.[0];
  const { iClass, targetYear } = rdfData;
  return (
    iClass === prevRecommendation?.iClass &&
    targetYear !== prevRecommendation?.targetYear
  );
};

export const isClosedFund: StateTester = (_, nextAppState): boolean =>
  Boolean(nextAppState?.store?.loadedData?.closedTickers?.length > 0);

export const chosenClosedFundInFONASU: StateTester = (
  _,
  nextAppState
): boolean =>
  Boolean(nextAppState?.store?.loadedData?.closedTickers?.length > 0) &&
  nextAppState?.chosen?.section === 'portfolio';

export const hasMultipleOCRPlans: StateTester = (_, nextAppState): boolean =>
  Boolean(nextAppState?.store?.loadedData?.rpsPlans?.planNumbers?.length > 1);

export const successfullyLinkedBank: StateTester = (_, nextAppState): boolean =>
  Boolean(nextAppState?.store?.loadedData?.plaidResult?.success);

export const bankAccountAmountTooLow: StateTester = (
  _,
  nextAppState
): boolean =>
  Boolean(nextAppState?.store?.loadedData?.plaidResult?.amountTooLow);

export const isSwitchToCheck = (loadedData): boolean =>
  Boolean(
    loadedData.responseCode?.auxData?.auxData ===
      BankFailureTypes.SWITCHTOCHECK ||
      loadedData.responseCode?.auxData?.ACTION ===
        BankFailureTypes.SWITCHFUNDINGTOCHECK
  );

export const chosenOCRFundSection: StateTester = (_, nextAppState): boolean =>
  nextAppState?.chosen?.section === 'ocrChooseFunds';

export const noSSNInRPSPlans: StateTester = (_, nextAppState): boolean =>
  Boolean(nextAppState?.store?.loadedData?.noSSNInRPSPlans);

export const maxedOutContributions: StateTester = (
  _,
  nextAppState
): boolean => {
  const enabled = nextAppState?.store?.enabled;
  const currentYearContributionsSoFar =
    nextAppState?.store?.loadedData?.contributionLimits
      ?.currentYearContributionsSoFar;
  const currentYearMaximumLimit =
    nextAppState?.store?.loadedData?.contributionLimits
      ?.currentYearMaximumLimit;

  return Boolean(
    enabled && currentYearContributionsSoFar >= currentYearMaximumLimit
  );
};

export const guestUserLoginRequired: StateTester = (_, nextState) => {
  const { guestUserApponFile } = nextState?.store?.loadedData || false;
  return Boolean(guestUserApponFile);
};

export const meetsIClassEligibility = (fund: any): boolean =>
  !fund.iClass &&
  fund.correspondingFund?.iClass &&
  fund.amount >= I_CLASS_MINIMUM;

export const showUpdateToIClassModal = (_, nextState): boolean => {
  const { app, chosenInvestments = [], updateToIClassModalShown } =
    nextState?.store || {};
  const filteredNewInvestments = filterExistingInvestmentsFromNew(
    app?.existingAccounts,
    chosenInvestments
  );

  return (
    !updateToIClassModalShown &&
    (meetsSummitRewardsTierForIClass(_, nextState) ||
      filteredNewInvestments.some(fund => meetsIClassEligibility(fund)))
  );
};

/**
 * Function which returns status of user name
 * @returns {boolean}
 * */
export const isUserNameMissing: StateTester = (_, nextAppState): boolean =>
  !nextAppState?.store?.loadedData?.customerId;

/**
 * Determines if the customer or any joint owner is a resident of Louisiana (LA).
 *
 * @param _ - Unused parameter.
 * @param nextAppState - The next application state containing customer and joint owners information.
 * @returns `true` if the customer's residential address state is 'LA' or if any joint owner's residential address state is 'LA'.
 */
export const isLAResidentRouteTester: StateTester = withDescription(
  (_, nextAppState): boolean => {
    const { customer = {}, jointOwners = [] } = nextAppState?.store?.app || {};
    const jointOwnerLAResident = jointOwners?.some(
      jo => jo.residentialAddress?.state === 'LA'
    );
    return customer.residentialAddress?.state === 'LA' || jointOwnerLAResident;
  },
  'Account opener or joint owner is a resident of Louisiana'
);

export const isLouisianaResident = (
  customer: any = {},
  jointOwners: any = []
): boolean => {
  const jointOwnerLAResident =
    !isEmptyObject(jointOwners) &&
    jointOwners?.some(owner => owner.residentialAddress?.state === 'LA');

  return customer.residentialAddress?.state === 'LA' || jointOwnerLAResident;
};
